import PropTypes from 'prop-types';
import {
  Box, Typography, Paper, Stack, Divider, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import { fNumber } from 'utils/formatNumber';

function formatDateTime(dt) {
  const d = new Date(dt);
  return d.toLocaleString();
}

export default function GiftcardAuditTrailList({ auditTrails, order }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!auditTrails || auditTrails.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No audit trail records found for this order.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        // maxHeight: 400,
        // overflowY: 'auto',
        pr: 1,
        pb: 1,
      }}
    >
      {auditTrails.map((row, idx) => (
        <Paper
          key={row.id}
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: idx === 0 ? theme.palette.action.hover : 'background.paper',
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            boxShadow: 0,
          }}
        >
          {/* Staff & Date */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" fontWeight={600}>
              {row.staff_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(row.created_at)}
            </Typography>
          </Stack>

          {/* Financial Numbers */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1, mb: 1 }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Amount
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                // color="success.main"
                sx={{ lineHeight: 1.1 }}
              >
                {order.currency}{fNumber(row.amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Settlement Amount
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                color="info.main"
                sx={{ lineHeight: 1.1 }}
              >
                {order.settlement_currency}{fNumber(row.settlement_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                # Cards
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                color="warning.main"
                sx={{ lineHeight: 1.1 }}
              >
                {row.number_of_cards}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1 }} />

          {/* Remark (de-emphasized) */}
          <Box>
            <Typography variant="caption" color="text.disabled">
              Remark
            </Typography>
            <Tooltip title={row.remark} arrow>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{
                  whiteSpace: 'pre-line',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: 40,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  mt: 0.5,
                  fontSize: '0.92rem',
                }}
              >
                {row.remark}
              </Typography>
            </Tooltip>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
}

GiftcardAuditTrailList.propTypes = {
  auditTrails: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    order_id: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    settlement_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    number_of_cards: PropTypes.number.isRequired,
    remark: PropTypes.string.isRequired,
    staff_name: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired,
  })).isRequired,
  order: PropTypes.object.isRequired,
};
